import React, { useState } from "react";
import { serviceConsumer } from "../../../../network/ServiceConsumer";
import { ShowToastMessage } from "../../../../helper/ShowToastMessage";

const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050,
};

const cardStyle = {
    background: "#fff",
    borderRadius: "8px",
    padding: "24px",
    width: "480px",
    maxWidth: "92vw",
    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
};

const cancelBtn = {
    padding: "6px 16px",
    border: "1px solid #ccc",
    background: "#fff",
    borderRadius: "4px",
    cursor: "pointer",
};

const dangerBtn = {
    padding: "6px 16px",
    border: "none",
    background: "#b00020",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer",
};

const primaryBtn = {
    padding: "6px 16px",
    border: "none",
    background: "#68AD3F",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer",
};

const selectStyle = {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #ccc",
    borderRadius: "4px",
};

// Admin-only repair tool for tabulation data entered under the wrong
// adjudicator / round / competition: delete everything the selected judge
// entered (in the selected round for 6/9-judge comps), or move it to the
// corrected judge and/or round, keeping the scores themselves intact.
const BulkScoreFixModal = ({
    compId,
    compLabel,
    judgeId,
    judgeName,
    round, // string for slot-mode comps; null/"" for legacy (= all rounds)
    isSlotComp,
    scoreCount,
    adjudicators,
    onClose,
    onDone,
}) => {
    const [mode, setMode] = useState("delete");
    const [targetJudge, setTargetJudge] = useState(judgeId);
    const [targetRound, setTargetRound] = useState(round || "");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");

    const sourceLabel = isSlotComp ? `Round ${round}` : "all rounds";
    const judgeLabel = (adj) =>
        adj?.name || `${adj?.fname || ""} ${adj?.lname || ""}`.trim() || "-";

    const moveIsNoop =
        mode === "move" &&
        targetJudge === judgeId &&
        (!isSlotComp || String(targetRound) === String(round));

    const confirmHandler = async () => {
        try {
            setErr("");
            setSubmitting(true);
            const url = `${process.env.REACT_APP_BASE_URL}/feis/competition/scores/bulk-remove/${compId}`;
            const payload = {
                judge_id: judgeId,
                ...(isSlotComp ? { round: Number(round) } : {}),
                ...(mode === "move"
                    ? {
                        reassign_to: {
                            ...(targetJudge !== judgeId ? { judge_id: targetJudge } : {}),
                            ...(isSlotComp && String(targetRound) !== String(round)
                                ? { round: Number(targetRound) }
                                : {}),
                        },
                    }
                    : {}),
            };
            const response = await serviceConsumer("POST", url, payload);
            ShowToastMessage("success", response?.message || "Done");
            onDone();
        } catch (error) {
            setErr(error?.messages || "Something went wrong");
            setSubmitting(false);
        }
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
                <h4 style={{ marginBottom: "12px" }}>Fix adjudicator data</h4>
                <p style={{ fontSize: "14px", color: "#444" }}>
                    <strong>{scoreCount}</strong> score{scoreCount === 1 ? "" : "s"} entered by{" "}
                    <strong>{judgeName}</strong> in <strong>{sourceLabel}</strong> of{" "}
                    <strong>{compLabel}</strong>.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px", margin: "14px 0" }}>
                    <label style={{ fontSize: "14px", cursor: "pointer" }}>
                        <input
                            type="radio"
                            name="bulkmode"
                            checked={mode === "delete"}
                            onChange={() => setMode("delete")}
                            style={{ marginRight: "8px" }}
                        />
                        Delete these scores permanently
                    </label>
                    <label style={{ fontSize: "14px", cursor: "pointer" }}>
                        <input
                            type="radio"
                            name="bulkmode"
                            checked={mode === "move"}
                            onChange={() => setMode("move")}
                            style={{ marginRight: "8px" }}
                        />
                        Move them to the correct adjudicator{isSlotComp ? " / round" : ""}
                    </label>
                </div>

                {mode === "move" && (
                    <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: "12px", color: "#777", marginBottom: "4px" }}>Adjudicator</div>
                            <select
                                style={selectStyle}
                                value={targetJudge}
                                onChange={(e) => setTargetJudge(e.target.value)}
                            >
                                {adjudicators?.map((adj) => (
                                    <option key={adj?._id} value={adj?._id}>
                                        {judgeLabel(adj)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {isSlotComp && (
                            <div style={{ width: "110px" }}>
                                <div style={{ fontSize: "12px", color: "#777", marginBottom: "4px" }}>Round</div>
                                <select
                                    style={selectStyle}
                                    value={targetRound}
                                    onChange={(e) => setTargetRound(e.target.value)}
                                >
                                    {[1, 2, 3].map((r) => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                )}

                {mode === "delete" && (
                    <p style={{ fontSize: "13px", color: "#b00020" }}>
                        This cannot be undone. The scores will need to be re-entered.
                    </p>
                )}
                {moveIsNoop && (
                    <p style={{ fontSize: "13px", color: "#777" }}>
                        Pick a different adjudicator{isSlotComp ? " or round" : ""} to move to.
                    </p>
                )}
                {err && <p style={{ fontSize: "13px", color: "#b00020" }}>{err}</p>}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
                    <button onClick={onClose} disabled={submitting} style={cancelBtn}>Cancel</button>
                    <button
                        onClick={confirmHandler}
                        disabled={submitting || scoreCount === 0 || moveIsNoop}
                        style={{
                            ...(mode === "delete" ? dangerBtn : primaryBtn),
                            ...(submitting || scoreCount === 0 || moveIsNoop ? { opacity: 0.5, cursor: "not-allowed" } : {}),
                        }}
                    >
                        {submitting ? "Working..." : mode === "delete" ? "Delete scores" : "Move scores"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkScoreFixModal;
